import { useEffect, useMemo, useState } from 'react';
import Checkbox from '../../components/ui/checkbox';
import { cancelEventAttendance, getActiveEventAttendance } from '../../utils/cancelEventAttendance';
import ConfirmationModal from './ConfirmationModal';
import { toast } from 'sonner';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  payment: any;
  onSuccess: () => void;
};

const attendeeLabel = (attendee: any, index: number) =>
  attendee.name ||
  [attendee.firstName, attendee.lastName].filter(Boolean).join(' ') ||
  attendee.email ||
  `Attendee ${index + 1}`;

export default function CancelEventAttendanceModal({ open, setOpen, payment, onSuccess }: Props) {
  const attendees = useMemo(() => getActiveEventAttendance(payment), [payment]);
  const eligibleIDs = useMemo(
    () =>
      attendees.flatMap((attendee) =>
        attendee.confirmationID && !attendee.pendingRefundRequestID
          ? [attendee.confirmationID]
          : [],
      ),
    [attendees],
  );
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);
  const [operationID, setOperationID] = useState('');
  const [noRefundAcknowledged, setNoRefundAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPaid = payment?.paymentMethod !== 'FREE';

  useEffect(() => {
    if (!open) return;
    setSelectedIDs(eligibleIDs);
    setOperationID(crypto.randomUUID());
    setNoRefundAcknowledged(false);
    setIsSubmitting(false);
  }, [open, eligibleIDs]);

  const confirmDisabled =
    isSubmitting || !operationID || selectedIDs.length === 0 || (isPaid && !noRefundAcknowledged);

  return (
    <ConfirmationModal
      open={open}
      setOpen={setOpen}
      autoClose={false}
      title="Cancel attendance and reopen seats?"
      description="Choose the attendees to cancel. Their registration stays in payment history and their seats return to the Event."
      confirmLabel={isSubmitting ? 'Canceling…' : 'Cancel attendance'}
      confirmDisabled={confirmDisabled}
      closeDisabled={isSubmitting}
      variant="danger"
      onConfirm={async () => {
        setIsSubmitting(true);
        try {
          await cancelEventAttendance(
            payment.transactionID,
            selectedIDs,
            operationID,
            noRefundAcknowledged,
          );
          toast.success('Attendance canceled and seats reopened');
          setOpen(false);
          onSuccess();
        } catch (error: any) {
          toast.error(error?.message || 'Unable to cancel attendance');
          setIsSubmitting(false);
        }
      }}
    >
      <div className="mt-4 space-y-2 rounded-md border border-edge bg-surface-subtle p-3">
        {attendees.map((attendee, index) => {
          const confirmationID = attendee.confirmationID as string;
          const claimed = Boolean(attendee.pendingRefundRequestID);
          return (
            <div key={confirmationID} className="flex items-start justify-between gap-3">
              <Checkbox
                checked={selectedIDs.includes(confirmationID)}
                disabled={claimed || isSubmitting}
                ariaLabel={`Cancel ${attendeeLabel(attendee, index)}`}
                onChange={(checked) =>
                  setSelectedIDs((current) =>
                    checked
                      ? [...current, confirmationID]
                      : current.filter((id) => id !== confirmationID),
                  )
                }
                label={attendeeLabel(attendee, index)}
              />
              {claimed && <span className="text-xs text-ink-muted">Refund pending</span>}
            </div>
          );
        })}
      </div>
      {isPaid && (
        <div className="mt-3 rounded-md border border-status-amber-text/30 bg-status-amber-bg p-3">
          <Checkbox
            checked={noRefundAcknowledged}
            disabled={isSubmitting}
            onChange={setNoRefundAcknowledged}
            label="I understand this does not refund the payment."
          />
        </div>
      )}
    </ConfirmationModal>
  );
}
